/**
 * Макети екранів додавання конектора — на сторінці «MCP від inFLOW»
 * (owner 2026-11-01, дивлячись на екран Claude: «если мне тут нихуя не ясно,
 * другим будет ещё хуже»). Підписи полів — англійською, як у самих ШІ;
 * помаранчеві номери — ті самі кроки, що в списку над макетом.
 */
import type { ReactNode } from "react";
import { Icon } from "@/components/Icon";

function Marker({ n }: { n: number }) {
  return (
    <span className="grid h-[1.125rem] w-[1.125rem] shrink-0 place-items-center rounded-full bg-primary font-display text-[0.625rem] font-bold leading-none text-white">
      {n}
    </span>
  );
}

function Label({ children, step }: { children: ReactNode; step?: number }) {
  return (
    <div className="mb-1 flex items-center gap-1.5 text-[0.65625rem] font-semibold text-ink-dim">
      {children}
      {step !== undefined && <Marker n={step} />}
    </div>
  );
}

function Field({ children, mono }: { children: ReactNode; mono?: boolean }) {
  return (
    <div
      className={`truncate rounded-[0.5rem] border border-line bg-surface px-2.5 py-1.5 text-[0.71875rem] text-ink ${
        mono ? "font-mono [font-variant-ligatures:none]" : ""
      }`}
    >
      {children}
    </div>
  );
}

function Frame({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="rounded-[1rem] bg-bg p-2.5">
      <div className="rounded-[0.875rem] bg-surface p-3.5 shadow-card-soft">
        <div className="mb-3 font-display text-[0.8125rem] font-bold text-ink">{title}</div>
        <div className="flex flex-col gap-3">{children}</div>
      </div>
    </div>
  );
}

function shortUrl(url: string): string {
  return url ? url.replace(/(ifl_[0-9]+_)[^/]*$/, "$1…") : "https://…/api/mcp/ifl_…";
}

function Radio({ label, on, tag }: { label: string; on?: boolean; tag?: string }) {
  return (
    <div
      className={`flex items-center gap-2 rounded-[0.5rem] px-2.5 py-1.5 text-[0.71875rem] ${
        on ? "bg-primary/[0.07] text-ink ring-1 ring-primary" : "text-ink-faint"
      }`}
    >
      <span
        className={`grid h-[0.875rem] w-[0.875rem] shrink-0 place-items-center rounded-full border ${
          on ? "border-primary" : "border-ink/20"
        }`}
      >
        {on && <span className="h-[0.4375rem] w-[0.4375rem] rounded-full bg-primary" />}
      </span>
      <span className="flex-1">{label}</span>
      {tag && (
        <span className="rounded-full bg-success/10 px-1.5 py-0.5 text-[0.59375rem] font-semibold text-success">
          {tag}
        </span>
      )}
    </div>
  );
}

/** Claude: «Add custom connector» — як на телефоні й на сайті. */
export function ClaudeConnectorMock({ url }: { url: string }) {
  return (
    <Frame title="Add custom connector">
      <div>
        <Label step={3}>Name</Label>
        <Field>inFLOW</Field>
      </div>
      <div>
        <Label step={3}>Remote MCP server URL</Label>
        <Field mono>{shortUrl(url)}</Field>
      </div>
      <div>
        <Label step={4}>Authentication</Label>
        <div className="flex flex-col gap-1">
          <Radio label="Sign in now" />
          <Radio label="Sign in when needed" />
          <Radio label="No sign-in" tag="Detected" on />
        </div>
        <div className="mt-1.5 flex flex-col gap-1 rounded-[0.5rem] bg-[#FCF1D6] px-2.5 py-2">
          <div className="h-[0.3125rem] w-11/12 rounded-full bg-[#E9C977]" />
          <div className="h-[0.3125rem] w-7/12 rounded-full bg-[#E9C977]" />
        </div>
      </div>
      <div className="flex items-center justify-between text-[0.71875rem] text-ink-faint">
        <span>Request headers</span>
        <span>+ Add header</span>
      </div>
      <div className="-mt-1.5 flex items-center justify-between text-[0.71875rem] text-ink-faint">
        <span>Advanced</span>
        <span>›</span>
      </div>
      <div className="flex items-center justify-end gap-2 pt-0.5">
        <span className="rounded-full px-3 py-1.5 text-[0.71875rem] font-semibold text-ink-faint">Back</span>
        <span className="flex items-center gap-1.5">
          <Marker n={5} />
          <span className="rounded-full bg-ink px-4 py-1.5 text-[0.71875rem] font-semibold text-surface ring-2 ring-primary ring-offset-2 ring-offset-surface">
            Add
          </span>
        </span>
      </div>
    </Frame>
  );
}

/** ChatGPT: створення застосунку в режимі розробника. */
export function ChatGptAppMock({ url }: { url: string }) {
  return (
    <Frame title="New App">
      <div>
        <Label step={4}>Name</Label>
        <Field>inFLOW</Field>
      </div>
      <div>
        <Label step={4}>MCP Server URL</Label>
        <Field mono>{shortUrl(url)}</Field>
      </div>
      <div>
        <Label step={4}>Authentication</Label>
        <div className="flex items-center justify-between rounded-[0.5rem] bg-primary/[0.07] px-2.5 py-1.5 text-[0.71875rem] text-ink ring-1 ring-primary">
          <span>No authentication</span>
          <span className="text-ink-faint">▾</span>
        </div>
      </div>
      <div className="flex items-center gap-2 text-[0.71875rem] text-ink">
        <span className="grid h-[0.875rem] w-[0.875rem] shrink-0 place-items-center rounded-[0.25rem] bg-primary">
          <Icon name="check" size={10} color="#FFFFFF" />
        </span>
        <span className="flex-1">I trust this application</span>
        <Marker n={5} />
      </div>
      <div className="flex items-center justify-end gap-2 pt-0.5">
        <span className="rounded-full bg-ink px-4 py-1.5 text-[0.71875rem] font-semibold text-surface ring-2 ring-primary ring-offset-2 ring-offset-surface">
          Create
        </span>
      </div>
    </Frame>
  );
}
